import React, { useRef } from "react";
import Dropzone from "./Dropzone.web";
import FileDialog from "./FileDialog";
import Preview from "./Preview";
import { Attachment } from "../../../../types/common";
import Button from "@components/base/button";
import {
  generateButtonColors,
  overrideButtonStyles,
} from "@components/base/button/overrides";
import { css } from "@emotion/react";
import { useTranslation } from "react-i18next";
import { toAcceptedAttachments } from "./helpers";

interface AttachmentDropProps {
  attachments: Attachment[];
  onChange: (attachments: Attachment[]) => void;
  multiple?: boolean;
  withVideo?: boolean;
  disabled?: boolean;
}

const AttachmentDrop: React.FC<AttachmentDropProps> = ({
  attachments,
  onChange,
  multiple = true,
  withVideo = true,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);

  const onDrag = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (disabled) return;
    onAddAttachments(e.dataTransfer.files);
  };

  const onClick = () => {
    if (disabled) return;
    inputRef.current?.click();
  };

  const onInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onAddAttachments(event.target.files);
  };

  const onAddAttachments = async (fileList: FileList | null) => {
    if (fileList === null || disabled) return;

    const nextFiles = await toAcceptedAttachments(fileList, withVideo);
    if (!nextFiles.length) return;

    const nextAttachments = multiple
      ? [...attachments, ...nextFiles]
      : [nextFiles[0]];

    onChange(nextAttachments);
  };

  const onRemoveAttachment = (attachment: Attachment) => {
    const filtered = attachments.filter((x) => x.url !== attachment.url);
    onChange(filtered);
  };

  return (
    <>
      <FileDialog
        ref={inputRef}
        onInputChange={onInputChange}
        multiple={multiple}
        accept={withVideo ? "image/*,video/*" : "image/*"}
      />
      {!attachments?.length ? (
        <Dropzone
          onClick={onClick}
          onDrag={onDrag}
          onDrop={onDrop}
          withVideo={withVideo}
        />
      ) : (
        <div>
          <Button
            onClick={onClick}
            type="button"
            kind="secondary"
            disabled={disabled}
            overrides={overrideButtonStyles({
              BaseButton: ({ $theme, $disabled }) => ({
                marginBottom: "32px",
                width: "100%",
                ...generateButtonColors({
                  $disabled,
                  color: $theme.colors.info,
                }),
              }),
            })}>
            {withVideo ? t("issue.addAttachment") : t("issue.addImage")}
          </Button>
          <div css={styles.previewGrid}>
            {attachments.map((attachment) => (
              <Preview
                key={attachment.id ?? attachment.url}
                attachment={attachment}
                onRemoveImage={onRemoveAttachment}
              />
            ))}
          </div>
        </div>
      )}
    </>
  );
};

const styles = {
  previewGrid: css({
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    flexWrap: "wrap",
  }),
};

export default AttachmentDrop;
